import React, { useCallback } from 'react';
import './TreeNodeContent.less';
import classNames from 'classnames';
import { ITreeNode } from './TreeNode';
import { IconButton } from 'components/buttons/IconButton';
import { Spinner } from 'components/Spinner';
import { TreeNodeMouseEventHandler } from '../Legacy/useTreeModel';

export interface ITreeNodeContentProps<T> {
  readonly node: ITreeNode<T>;
  title?: string;
  className?: string;
  chevronClassName?: string;
  hasChildren?: boolean;
  chevron?: React.ReactNode;
  onClick?: TreeNodeMouseEventHandler<T>;
  onMouseUp?: TreeNodeMouseEventHandler<T>;
  onMouseDown?: TreeNodeMouseEventHandler<T>;
  // TODO: use TreeNodeMouseEventHandler<T> type to be consistent with other event handlers
  onExpandNodeClick?: (nodeId: string, isExpanded: boolean, element: HTMLElement) => void;
}

//TODO need to use "node--is-leaf" class but it works bad right now
function getNodeContentClassName(hasChildren?: boolean, className?: string) {
  return classNames('dpc-tree-node__content', className, {
    'dpc-tree-node__content--no-children': !hasChildren,
  });
}

function getChevronClassName(className?: string, isExpanded?: boolean) {
  return classNames('dpc-tree-node__chevron-icon', className, { 'dpc-tree-node__chevron-icon--expanded': isExpanded });
}

/**
 * @deprecated Please use /CommonTree/CommonTreeNode.tsx
 */
export function TreeNodeContent<T>({
  node,
  children,
  chevronClassName,
  className,
  chevron,
  hasChildren,
  onExpandNodeClick,
  onMouseDown,
  onMouseUp,
  onClick,
  title,
}: React.PropsWithChildren<ITreeNodeContentProps<T>>) {
  const handleChevronMouseEvent: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    event => {
      event.stopPropagation();
      onExpandNodeClick && onExpandNodeClick(node.nodeId, !node.isExpanded, event.target as HTMLElement);
    },
    [onExpandNodeClick, node.isExpanded, node.nodeId]
  );

  const handleMouseEvent: React.MouseEventHandler<HTMLDivElement> = useCallback(
    e => {
      switch (e.type) {
        case 'mousedown':
          onMouseDown?.(node, e);
          break;
        case 'mouseup':
          onMouseUp?.(node, e);
          break;
        case 'click':
          onClick?.(node, e);
          break;
      }
    },
    [node, onClick, onMouseDown, onMouseUp]
  );

  return (
    <div
      className={getNodeContentClassName(hasChildren, className)}
      onClick={handleMouseEvent}
      onMouseDown={handleMouseEvent}
      onMouseUp={handleMouseEvent}
      title={title}
    >
      {hasChildren &&
        (chevron ??
          (node.isLoading ? (
            <span className="dpc-tree-node__loading">
              <Spinner size={14} />
            </span>
          ) : (
            <IconButton
              aria-label="Expand Node"
              className="dpc-tree-node__chevron"
              iconClassName={getChevronClassName(chevronClassName, node.isExpanded)}
              onMouseDown={handleChevronMouseEvent}
            />
          )))}
      {children}
    </div>
  );
}
